import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { BiArrowToLeft, BiCheck, BiX, BiUser } from 'react-icons/bi'
import { agencyService, Agency } from '../services/agencyService'
import { useCurrentUser } from '../hooks/useCurrentUser'
import Alert from '../components/Alert'

const AgencyJoin = () => {
  const { agencyCode } = useParams<{ agencyCode: string }>()
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const [agency, setAgency] = useState<Agency | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isJoining, setIsJoining] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('')
  const [existingInvite, setExistingInvite] = useState<any>(null)

  useEffect(() => {
    if (agencyCode) {
      fetchAgencyData()
    }
  }, [agencyCode])

  if (!agencyCode) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>Invalid agency code. Please check the invite link and try again.</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/agencies')}
                  className="inline-flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg font-medium transition-colors"
                >
                  <BiArrowToLeft size={16} />
                  <span>Back to Agencies</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const fetchAgencyData = async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Get agency by code using the new endpoint
      const foundAgency = await agencyService.getAgencyByCode(agencyCode)
      setAgency(foundAgency)

      // Check if user already has an invite for this agency
      if (user) {
        try {
          const userInvites = await agencyService.getUserInvites()
          const existingInvite = userInvites.find(invite => invite.agencyId === foundAgency.id)
          setExistingInvite(existingInvite)
        } catch (err) {
          console.error('Error fetching user invites:', err)
        }
      }
    } catch (err: any) {
      console.error('Error fetching agency data:', err)
      setError('Agency not found. Please check the invite link and try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleJoinRequest = async () => {
    if (!user || !agency) return

    try {
      setIsJoining(true)
      setError(null)

      // Send join request using the new API
      await agencyService.requestToJoinAgency(agency.agencyCode, {
        role: 'agent',
        paymentResponsibility: 'agency'
      })

      setMessage('Your request to join this agency has been sent successfully! The agency admin will review your request.')
      setMessageType('success')

      // Redirect to agencies page after a short delay
      setTimeout(() => {
        navigate('/agencies')
      }, 3000)
    } catch (err: any) {
      console.error('Error sending join request:', err)
      setError(err?.message || 'Failed to send join request. Please try again.')
      setMessageType('error')
    } finally {
      setIsJoining(false)
    }
  }

  const handleAcceptInvite = async () => {
    if (!user || !existingInvite) return

    try {
      setIsJoining(true)
      setError(null)

      await agencyService.acceptInvite(existingInvite.agencyId, user.id || '')
      setMessage('You have successfully joined the agency!')
      setMessageType('success')

      // Redirect to the agency page after a short delay
      setTimeout(() => {
        navigate(`/agencies/${existingInvite.agencyId}`)
      }, 2000)
    } catch (err: any) {
      console.error('Error accepting invite:', err)
      setError(err?.message || 'Failed to accept invitation. Please try again.')
      setMessageType('error')
    } finally {
      setIsJoining(false)
    }
  }

  const handleRejectInvite = async () => {
    if (!user || !existingInvite) return

    try {
      setIsJoining(true)
      setError(null)

      await agencyService.rejectInvite(existingInvite.agencyId, user.id || '')
      setMessage('You have declined the invitation.')
      setMessageType('success')

      // Redirect to agencies page after a short delay
      setTimeout(() => {
        navigate('/agencies')
      }, 2000)
    } catch (err: any) {
      console.error('Error rejecting invite:', err)
      setError(err?.message || 'Failed to reject invitation. Please try again.')
      setMessageType('error')
    } finally {
      setIsJoining(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error && !agency) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error</h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate('/agencies')}
                  className="inline-flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg font-medium transition-colors"
                >
                  <BiArrowToLeft size={16} />
                  <span>Back to Agencies</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!agency) {
    return null
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Alert */}
      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {message && (
        <Alert
          type={messageType as 'success' | 'error'}
          message={message}
          onClose={() => setMessage('')}
        />
      )}

      {/* Join Actions */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        {!user ? (
          <div className="text-center">
            <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <BiUser className="text-yellow-600 dark:text-yellow-400" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Sign in to Join
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              You need to sign in to your account to join this agency.
            </p>
            <button
              onClick={() => navigate('/auth/login')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              <BiUser size={18} />
              Sign In
            </button>
          </div>
        ) : existingInvite ? (
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <BiUser className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              You have a pending invitation
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              You have been invited to join this agency. Would you like to accept or decline?
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleAcceptInvite}
                disabled={isJoining}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg font-medium transition-colors"
              >
                {isJoining ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Accepting...
                  </>
                ) : (
                  <>
                    <BiCheck size={18} />
                    Accept Invitation
                  </>
                )}
              </button>
              <button
                onClick={handleRejectInvite}
                disabled={isJoining}
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
              >
                {isJoining ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Declining...
                  </>
                ) : (
                  <>
                    <BiX size={18} />
                    Decline Invitation
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <BiUser className="text-primary-600 dark:text-primary-400" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Join {agency.name}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              You are requesting to join this agency. The agency admin will review your request.
            </p>
            <button
              onClick={handleJoinRequest}
              disabled={isJoining}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg font-medium transition-colors"
            >
              {isJoining ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending Request...
                </>
              ) : (
                <>
                  <BiCheck size={18} />
                  Request to Join
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AgencyJoin 